import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Grid,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AttachMoney, CalendarToday } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Property, Unit, unitApi } from '../services/api';
import { AccountingService, ChartOfAccount, CreateTransactionRequest } from '../services/accountingService';
import { getApiUrl } from '../config/api';

interface ExpenseDialogProps {
  open: boolean;
  onClose: () => void;
  property?: Property;
  unit?: Unit;
  onTransactionCreated?: () => void; // Optional callback for when transaction is created
}

const ExpenseDialog: React.FC<ExpenseDialogProps> = ({ open, onClose, property, unit, onTransactionCreated }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    date: new Date(),
    description: '',
    amount: '',
    accountId: '',
    notes: '',
  });
  
  const [expenseAccounts, setExpenseAccounts] = useState<ChartOfAccount[]>([]);
  const [cashAccountId, setCashAccountId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allUnits, setAllUnits] = useState<Unit[]>([]);

  // Fetch currencies to get currency symbol if property doesn't have it
  const { data: currencies = [] } = useQuery({
    queryKey: ['currencies'],
    queryFn: async () => {
      const response = await fetch(getApiUrl('/currencies'), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch currencies');
      }
      
      return response.json();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Helper function to get property currency symbol
  const getPropertyCurrencySymbol = () => {
    // First try to get from property.currency relationship
    if (property?.currency?.symbol) {
      return property.currency.symbol;
    }
    
    // If property has currency_id but no currency object, find it in currencies array
    if (property?.currency_id && Array.isArray(currencies)) {
      const currency = currencies.find((c: any) => c.id === property.currency_id);
      if (currency?.symbol) {
        return currency.symbol;
      }
    }
    
    // Fallback to default currency
    if (Array.isArray(currencies)) {
      const defaultCurrency = currencies.find((c: any) => c.is_default);
      if (defaultCurrency?.symbol) {
        return defaultCurrency.symbol;
      }
    }
    
    // Ultimate fallback
    return '$';
  };

  // Helper function to get property currency ID
  const getPropertyCurrencyId = () => {
    // First try to get from property.currency relationship
    if (property?.currency?.id) {
      return property.currency.id;
    }
    
    // If property has currency_id, use it directly
    if (property?.currency_id) {
      return property.currency_id;
    }
    
    // Fallback to default currency
    if (Array.isArray(currencies)) {
      const defaultCurrency = currencies.find((c: any) => c.is_default);
      if (defaultCurrency?.id) {
        return defaultCurrency.id;
      }
    }
    
    // Ultimate fallback to 1 (usually USD)
    return 1;
  };

  // Load expense accounts and units when dialog opens
  useEffect(() => {
    if (open) {
      console.log('ExpenseDialog opened with property:', property?.id);
      
      if (property) {
        loadExpenseAccounts();
        loadAllUnits();
      } else {
        console.warn('ExpenseDialog opened without property context');
        setError('No property selected. Please try again.');
      }
    }
  }, [open, property]);

  const loadExpenseAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Loading expense accounts for property:', property?.id);
      
      // For now, let's always use fallback accounts to test the functionality
      console.log('Using fallback accounts for testing...');
      const fallbackAccounts = [
        { id: 44, code: '5000', name: 'Maintenance and Repairs', account_type: 'EXPENSE', normal_balance: 'DEBIT', is_active: true, description: 'Property maintenance and repairs' },
        { id: 45, code: '5100', name: 'Utilities', account_type: 'EXPENSE', normal_balance: 'DEBIT', is_active: true, description: 'Electricity, gas, water, etc.' },
        { id: 50, code: '5200', name: 'Insurance', account_type: 'EXPENSE', normal_balance: 'DEBIT', is_active: true, description: 'Property insurance premiums' },
        { id: 51, code: '5300', name: 'Property Management Fees', account_type: 'EXPENSE', normal_balance: 'DEBIT', is_active: true, description: 'Management company fees' },
        { id: 57, code: '5900', name: 'Other Expenses', account_type: 'EXPENSE', normal_balance: 'DEBIT', is_active: true, description: 'Miscellaneous expenses' },
      ];
      setExpenseAccounts(fallbackAccounts as ChartOfAccount[]);
      console.log('Fallback accounts set:', fallbackAccounts.length);
      
      // Still try to load from API in background for debugging
      try {
        let accounts = await AccountingService.getChartOfAccounts(property?.id);
        console.log('API accounts received (with property filter):', accounts);
        
        if (!accounts || accounts.length === 0) {
          accounts = await AccountingService.getChartOfAccounts();
          console.log('API accounts received (without property filter):', accounts);
        }
        
        const apiExpenseAccounts = (accounts || []).filter(account => account.account_type === 'EXPENSE');
        console.log('API expense accounts:', apiExpenseAccounts);
        
        // Find cash account for journal entries
        const cashAccount = (accounts || []).find(account => account.account_type === 'ASSET' && account.code === '1000');
        if (cashAccount) {
          setCashAccountId(cashAccount.id);
          console.log('Found cash account ID:', cashAccount.id);
        } else {
          // Set fallback cash account ID (26 is confirmed Cash account)
          setCashAccountId(26);
          console.log('Using fallback cash account ID: 26');
        }
        
        if (apiExpenseAccounts && apiExpenseAccounts.length > 0) {
          console.log('API returned expense accounts, switching to real data');
          setExpenseAccounts(apiExpenseAccounts);
          setError(null);
        }
      } catch (apiError) {
        console.error('API call failed:', apiError);
        setError(`API unavailable: ${apiError instanceof Error ? apiError.message : 'Unknown error'}. Using test categories.`);
      }
      
    } catch (error) {
      console.error('Failed to load expense accounts:', error);
      setError(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUnits = async () => {
    try {
      const units = await unitApi.getAll();
      setAllUnits(units || []);
    } catch (error) {
      console.error('Failed to load units:', error);
      setAllUnits([]);
    }
  };

  // Helper function to get hierarchical unit name by unit ID
  const getUnitHierarchyNameById = (unitId: number): string => {
    const unitData = allUnits?.find((u: any) => u.id === unitId);
    if (!unitData) return 'Unknown Unit';
    
    // Build parent path using units array
    const buildParentPath = (currentUnit: any): string => {
      const parent = allUnits?.find((u: any) => u.id === currentUnit.parent_unit_id);
      if (!parent) return currentUnit.unit_number;
      
      const parentPath = buildParentPath(parent);
      return `${parentPath} → ${currentUnit.unit_number}`;
    };
    
    return buildParentPath(unitData);
  };

  const createExpenseMutation = useMutation({
    mutationFn: async (data: CreateTransactionRequest) => {
      return AccountingService.createTransaction(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounting-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['accounting-dashboard'] });
      
      // Call the optional callback if provided
      if (onTransactionCreated) {
        onTransactionCreated();
      }
      
      // Emit a global event that other components can listen to
      window.dispatchEvent(new CustomEvent('transactionCreated', { 
        detail: { source: 'ExpenseDialog' } 
      }));
      
      handleClose();
    },
    onError: (error: any) => {
      console.error('Failed to create expense:', error);
      setError('Failed to create expense. Please try again.');
    },
  });

  const handleClose = () => {
    setFormData({
      date: new Date(),
      description: '',
      amount: '',
      accountId: '',
      notes: '',
    });
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!property || !formData.description || !formData.amount || !formData.accountId) {
      setError('Please fill in all required fields.');
      return;
    }

    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    try {
      setError(null);
      
      // Find the selected account
      const selectedAccount = expenseAccounts.find(acc => acc.id === parseInt(formData.accountId));
      if (!selectedAccount) {
        setError('Selected account not found.');
        return;
      }

      // Create the transaction data
      const transactionData: CreateTransactionRequest = {
        transaction_date: formData.date.toISOString().split('T')[0],
        description: formData.description + (formData.notes ? ` - ${formData.notes}` : ''),
        transaction_type: 'EXPENSE',
        property_id: property.id,
        unit_id: unit?.id,
        unit_number: unit?.unit_number,
        unit_name: unit?.name,
        currency_id: getPropertyCurrencyId(), // Use the new helper function
        journal_entries: [
          {
            account_id: parseInt(formData.accountId),
            debit_amount: amount,
            description: formData.description,
          },
          {
            account_id: cashAccountId || 26, // Use dynamic cash account ID or fallback to 26 (Cash account)
            credit_amount: amount,
            description: `Cash payment for ${formData.description}`,
          },
        ],
        auto_post: true,
      };

      console.log('💰 Creating expense transaction:', {
        expenseAccountId: parseInt(formData.accountId),
        cashAccountId: cashAccountId || 26,
        amount: amount,
        description: formData.description,
        property_id: property.id,
        unit_id: unit?.id,
        unit_number: unit?.unit_number,
        unit_name: unit?.name
      });

      await createExpenseMutation.mutateAsync(transactionData);
    } catch (error) {
      console.error('Error creating expense:', error);
    }
  };

  const targetName = property 
    ? (unit ? `${property.name} - ${getUnitHierarchyNameById(unit.id)}` : property.name)
    : 'No property selected';

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <AttachMoney color="primary" />
            Add Expense
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {targetName}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              <Button 
                size="small" 
                onClick={loadExpenseAccounts}
                sx={{ ml: 1 }}
              >
                Retry
              </Button>
            </Alert>
          )}
          
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Expense Date"
                  value={formData.date}
                  onChange={(newValue) => {
                    if (newValue) {
                      setFormData(prev => ({ ...prev, date: newValue }));
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday fontSize="small" />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Amount"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                          {getPropertyCurrencySymbol()}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth required disabled={expenseAccounts.length === 0}>
                  <InputLabel>Expense Category</InputLabel>
                  <Select
                    value={formData.accountId}
                    label="Expense Category"
                    onChange={(e) => setFormData(prev => ({ ...prev, accountId: e.target.value }))}
                  >
                    {expenseAccounts.length === 0 ? (
                      <MenuItem value="" disabled>
                        {loading ? 'Loading categories...' : 'No expense categories available'}
                      </MenuItem>
                    ) : (
                      expenseAccounts.map((account) => (
                        <MenuItem key={account.id} value={account.id}>
                          {account.code} - {account.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                  {expenseAccounts.length === 0 && !loading && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
                      Debug: Accounts loaded = {expenseAccounts.length}, Loading = {loading.toString()}, Property ID = {property?.id || 'none'}, Unit ID = {unit?.id || 'none'}
                      <br />
                      Check browser console for detailed logs
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Plumbing repair, Electricity bill, etc."
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Additional Notes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  multiline
                  rows={2}
                  placeholder="Optional additional information..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || createExpenseMutation.isPending || expenseAccounts.length === 0}
            startIcon={createExpenseMutation.isPending ? <CircularProgress size={20} /> : <AttachMoney />}
          >
            {createExpenseMutation.isPending ? 'Creating...' : 'Add Expense'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ExpenseDialog;
